import { useCallback, useEffect, useMemo, useState } from 'react';

import { useUsers, useRoles, useCompanies } from '@adsk/alloy-provider';

import {
  UseMentionSuggestionFilter,
  UseMentionSuggestionToMentionData,
  UseMentionSuggestionTypeToSize,
} from './types';
import { getSuggestionsByType, searchBySuggestionType } from './utils';
import {
  MentionData,
  MENTION_TYPES,
  defaultFilter,
  defaultToMentionData,
  defaultTypeToSize,
} from '../../consts';
import { useMentionRecents } from '../useMentionRecents';

export interface MentionRecentsOptions {
  /** When true, hides the Recents section. Default: true (disabled) during experimental phase. */
  disabledRecents?: boolean;
  /** Scopes recents storage per field for multiple Mention instances on the same page. */
  recentsFieldId?: string;
}

export interface UseMentionSuggestionsOptions {
  filter?: UseMentionSuggestionFilter;
  toMentionData?: UseMentionSuggestionToMentionData;
  typeToSize?: UseMentionSuggestionTypeToSize;
  onAddMention?: (mention: MentionData) => void;
}

function useMentionSuggestions(
  options?: UseMentionSuggestionsOptions & MentionRecentsOptions,
) {
  const {
    filter = defaultFilter,
    toMentionData = defaultToMentionData,
    typeToSize = defaultTypeToSize,
    disabledRecents = true,
    recentsFieldId,
    onAddMention,
  } = options || {};
  const [suggestions, setSuggestions] = useState<MentionData[]>([]);
  const [search, setSearch] = useState('');
  const users = useUsers();
  const roles = useRoles();
  const companies = useCompanies();

  // Uncapped suggestions for recents resolution (capped lists hide items beyond the display limit).
  const allSuggestions = useMemo(() => {
    if (disabledRecents) return [];
    return [
      ...toMentionData(
        filter(users.data, MENTION_TYPES.USER),
        MENTION_TYPES.USER,
      ),
      ...toMentionData(
        filter(companies.data, MENTION_TYPES.COMPANY),
        MENTION_TYPES.COMPANY,
      ),
      ...toMentionData(
        filter(roles.data, MENTION_TYPES.ROLE),
        MENTION_TYPES.ROLE,
      ),
    ];
  }, [
    disabledRecents,
    toMentionData,
    filter,
    users.data,
    companies.data,
    roles.data,
  ]);

  const { recentSuggestions, onAddMention: onAddMentionRecents } =
    useMentionRecents({
      enabled: !disabledRecents,
      fieldId: recentsFieldId,
      suggestions: allSuggestions,
      search,
      onAddMention,
    });

  const userSuggestions = useMemo(
    () =>
      getSuggestionsByType(
        users.data,
        MENTION_TYPES.USER,
        filter,
        typeToSize,
        toMentionData,
      ),
    [toMentionData, filter, users.data, typeToSize],
  );

  const companySuggestions = useMemo(
    () =>
      getSuggestionsByType(
        companies.data,
        MENTION_TYPES.COMPANY,
        filter,
        typeToSize,
        toMentionData,
      ),
    [toMentionData, filter, companies.data, typeToSize],
  );

  const roleSuggestions = useMemo(
    () =>
      getSuggestionsByType(
        roles.data,
        MENTION_TYPES.ROLE,
        filter,
        typeToSize,
        toMentionData,
      ),
    [toMentionData, filter, roles.data, typeToSize],
  );

  const searchUserSuggestions = useCallback(
    (searchQuery: string) =>
      searchBySuggestionType(
        searchQuery,
        users.data,
        MENTION_TYPES.USER,
        filter,
        typeToSize,
        toMentionData,
      ),
    [filter, toMentionData, typeToSize, users.data],
  );

  const searchCompanySuggestions = useCallback(
    (searchQuery: string) =>
      searchBySuggestionType(
        searchQuery,
        companies.data,
        MENTION_TYPES.COMPANY,
        filter,
        typeToSize,
        toMentionData,
      ),
    [toMentionData, filter, companies.data, typeToSize],
  );

  const searchRoleSuggestions = useCallback(
    (searchQuery: string) =>
      searchBySuggestionType(
        searchQuery,
        roles.data,
        MENTION_TYPES.ROLE,
        filter,
        typeToSize,
        toMentionData,
      ),
    [toMentionData, filter, roles.data, typeToSize],
  );

  const onSearchChange = useCallback(
    ({ value }: { value: string }) => {
      setSearch(value);
      setSuggestions([
        ...searchUserSuggestions(value),
        ...searchCompanySuggestions(value),
        ...searchRoleSuggestions(value),
      ]);
    },
    [searchCompanySuggestions, searchRoleSuggestions, searchUserSuggestions],
  );

  useEffect(() => {
    if (!search) {
      setSuggestions([
        ...userSuggestions,
        ...companySuggestions,
        ...roleSuggestions,
      ]);
    } else {
      onSearchChange({ value: search });
    }
  }, [
    userSuggestions,
    companySuggestions,
    roleSuggestions,
    search,
    searchUserSuggestions,
    searchCompanySuggestions,
    searchRoleSuggestions,
    toMentionData,
    onSearchChange,
    users.loading,
    companies.loading,
    roles.loading,
  ]);

  const suggestionsWithRecents = useMemo(
    () => [...recentSuggestions, ...suggestions],
    [recentSuggestions, suggestions],
  );

  return {
    isLoading: users.loading || companies.loading || roles.loading,
    suggestions: suggestionsWithRecents,
    onSearchChange,
    onAddMention: onAddMentionRecents,
  };
}

export default useMentionSuggestions;
